
import React from 'react';
import { Shield, Brain, Globe, Lock, Cpu, Zap, Activity, ShieldCheck, Terminal, Layers, Database, Code, ShoppingCart, Layout, ArrowUpRight } from 'lucide-react';
import AIGeneratedImage from './AIGeneratedImage';

const DetailedServices: React.FC = () => {
  return (
    <div className="pt-32 pb-40">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header Section */}
        <div className="flex flex-col items-center text-center mb-32 space-y-8">
          <div className="inline-flex items-center gap-3 px-5 py-2 rounded-full bg-brand-500/10 border border-brand-500/20 backdrop-blur-3xl glint">
            <span className="text-[10px] font-black text-brand-300 uppercase tracking-[0.4em] font-mono">
              SERVICE PROTOCOLS • FULL DISCLOSURE
            </span>
          </div>
          <h1 className="text-6xl md:text-8xl font-black text-white tracking-tighter leading-none">
            Our <span className="premium-gradient">Services</span>
          </h1>
          <p className="text-xl md:text-2xl text-slate-400 max-w-4xl font-light leading-relaxed">
            Bridging Cognitive Excellence with Unshakeable Security. At Sentrix Astra Technology, we build the infrastructure for the next era of global industry.
          </p>
        </div>

        {/* Section 1: Cybersecurity & AI Security */}
        <section className="mb-48">
          <div className="grid lg:grid-cols-2 gap-20 items-center mb-16">
            <div className="space-y-8">
              <div className="flex items-center gap-4 text-brand-400">
                <Shield className="w-12 h-12" />
                <h2 className="text-4xl md:text-5xl font-black tracking-tight text-white">Cybersecurity & AI Security</h2>
              </div>
              <p className="text-lg text-slate-400 font-light leading-relaxed">
                Holistic protection for the modern threat landscape. Our defensive matrices are designed to stay ahead of quantum-era vulnerabilities.
              </p>
            </div>
            <div className="aspect-[16/9] rounded-[3rem] overflow-hidden border border-white/5 relative glass-premium">
               <AIGeneratedImage 
                 prompt="Futuristic cybersecurity command center, glowing blue holographic screens, data streams, sleek obsidian furniture, hyper-realistic cinematic lighting"
                 alt="Cybersecurity Domain"
                 aspectRatio="16:9"
                 className="w-full h-full"
               />
               <div className="absolute inset-0 bg-gradient-to-t from-[#02040a] via-transparent to-transparent" />
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { 
                title: "SOC Build & Optimization", 
                desc: "We design and refine Security Operations Centers that act as the central nervous system of your defense, ensuring 24/7 vigilance.",
                icon: <Activity className="w-6 h-6" />
              },
              { 
                title: "Vulnerability Assessment & Penetration Testing", 
                desc: "Our elite 'Red Team' identifies and exploits weaknesses before malicious actors can, providing a roadmap to resilience.",
                icon: <Terminal className="w-6 h-6" />
              },
              { 
                title: "Security Evaluation", 
                desc: "A high-standard audit of your current posture, measuring your defenses against global compliance and future threats.",
                icon: <ShieldCheck className="w-6 h-6" />
              },
              { 
                title: "Incident Response Planning", 
                desc: "We engineer for the worst. We build rapid-action frameworks to neutralize threats and ensure business continuity.",
                icon: <Zap className="w-6 h-6" />
              },
              { 
                title: "AI Model & Data Security", 
                desc: "Shielding your intellectual property from prompt injection, data poisoning, and adversarial attacks.",
                icon: <Lock className="w-6 h-6" />
              },
              { 
                title: "Security Awareness Training", 
                desc: "Empowering your human firewall through simulation-based training that builds a culture of security.",
                icon: <Brain className="w-6 h-6" />
              }
            ].map((item, idx) => (
              <div key={idx} className="glass-card p-10 rounded-[2.5rem] border-beam group">
                <div className="w-12 h-12 rounded-2xl bg-brand-500/10 flex items-center justify-center mb-8 text-brand-400 border border-brand-500/20 transition-all group-hover:bg-brand-500 group-hover:text-black">
                  {item.icon}
                </div>
                <h4 className="text-xl font-black text-white mb-4 tracking-tight uppercase group-hover:text-brand-300 transition-colors">{item.title}</h4>
                <p className="text-slate-500 text-sm leading-relaxed group-hover:text-slate-300 transition-colors">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Section 2: AI Automation */}
        <section className="mb-48">
          <div className="grid lg:grid-cols-2 gap-20 items-center mb-16">
            <div className="order-2 lg:order-1 aspect-[16/9] rounded-[3rem] overflow-hidden border border-white/5 relative glass-premium">
               <AIGeneratedImage 
                 prompt="Advanced robotic AI brain core with purple electricity flowing through neural pathways, futuristic lab, soft glowing atmosphere, ultra high detail"
                 alt="AI Domain"
                 aspectRatio="16:9"
                 className="w-full h-full"
               />
               <div className="absolute inset-0 bg-gradient-to-t from-[#02040a] via-transparent to-transparent" />
            </div>
            <div className="order-1 lg:order-2 space-y-8">
              <div className="flex items-center gap-4 text-purple-400">
                <Brain className="w-12 h-12" />
                <h2 className="text-4xl md:text-5xl font-black tracking-tight text-white">AI Automation</h2>
              </div>
              <p className="text-lg text-slate-400 font-light leading-relaxed">
                Transforming operations with intelligent, secure workflows. We replace repetitive manual tasks with self-optimizing digital engines.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              { 
                title: "AI Workflow Automation", 
                desc: "Self-optimizing digital workflows that allow your team to focus on high-level cognitive strategy rather than repetitive tasks.",
                icon: <Cpu className="w-6 h-6" />
              },
              { 
                title: "Secure AI Chatbots", 
                desc: "Custom-built conversational agents interacting with your data in a closed, secure environment—no data leaks, elite support.",
                icon: <Layers className="w-6 h-6" />
              },
              { 
                title: "Business Process Automation (BPA)", 
                desc: "Re-engineering core operations into a seamless, automated engine that reduces overhead and eliminates human error.",
                icon: <Zap className="w-6 h-6" />
              },
              { 
                title: "Intelligent Analytics Systems", 
                desc: "Transform raw data into predictive insights with AI-driven dashboards that highlight trends before they occur.",
                icon: <Database className="w-6 h-6" />
              }
            ].map((item, idx) => (
              <div key={idx} className="glass-card p-12 rounded-[3rem] border-beam group flex gap-8">
                <div className="w-16 h-16 rounded-3xl bg-purple-500/10 shrink-0 flex items-center justify-center text-purple-400 border border-purple-500/20 transition-all group-hover:bg-purple-500 group-hover:text-black">
                  {item.icon}
                </div>
                <div>
                  <h4 className="text-2xl font-black text-white mb-4 tracking-tight uppercase group-hover:text-purple-300 transition-colors">{item.title}</h4>
                  <p className="text-slate-400 text-base leading-relaxed group-hover:text-slate-200 transition-colors">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Section 3: Web & Digital Solutions */}
        <section>
          <div className="grid lg:grid-cols-2 gap-20 items-center mb-16">
            <div className="space-y-8">
              <div className="flex items-center gap-4 text-brand-200">
                <Globe className="w-12 h-12" />
                <h2 className="text-4xl md:text-5xl font-black tracking-tight text-white">Web & Digital Solutions</h2>
              </div>
              <p className="text-lg text-slate-400 font-light leading-relaxed">
                Crafting future-ready digital platforms and experiences. High-standard aesthetics meeting uncompromising security by design.
              </p>
            </div>
            <div className="aspect-[16/9] rounded-[3rem] overflow-hidden border border-white/5 relative glass-premium">
               <AIGeneratedImage 
                 prompt="Clean futuristic web interface on floating glass panels, minimal white and silver aesthetics, elegant data visualization, soft high-key lighting"
                 alt="Digital Domain"
                 aspectRatio="16:9"
                 className="w-full h-full"
               />
               <div className="absolute inset-0 bg-gradient-to-t from-[#02040a] via-transparent to-transparent" />
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: "Corporate Websites", icon: <Layout /> },
              { title: "NGO Platforms", icon: <Globe /> },
              { title: "E-commerce Systems", icon: <ShoppingCart /> },
              { title: "Secure Portals", icon: <Code /> }
            ].map((item, idx) => (
              <div key={idx} className="glass-card p-10 rounded-[2.5rem] text-center group hover:bg-white/5 transition-all">
                <div className="w-14 h-14 rounded-2xl bg-white/5 flex items-center justify-center mx-auto mb-6 text-slate-400 group-hover:text-white transition-colors">
                  {/* Fixed TypeScript error by specifying any generic for React.cloneElement to allow 'size' property */}
                  {React.cloneElement(item.icon as React.ReactElement<any>, { size: 32 })}
                </div>
                <h4 className="text-lg font-black text-white uppercase tracking-tight mb-4">{item.title}</h4>
                <div className="w-8 h-0.5 bg-brand-500 mx-auto scale-x-0 group-hover:scale-x-100 transition-transform" />
              </div>
            ))}
          </div>

          <div className="mt-16 glass-premium p-12 rounded-[4rem] border-white/10 relative overflow-hidden group">
            <div className="hologram-line" />
            <div className="grid lg:grid-cols-2 gap-12 items-center relative z-10">
              <div className="space-y-6">
                <h3 className="text-3xl font-black text-white">Ready to Deploy?</h3>
                <p className="text-slate-400 leading-relaxed font-light">Our architects are ready to begin work on your custom digital infrastructure. Every detail is optimized for performance and integrity.</p>
              </div>
              <div className="flex justify-end">
                <button 
                  onClick={() => window.location.href = '#contact'}
                  className="px-14 py-6 rounded-full bg-brand-500 text-black font-black text-xs tracking-[0.3em] uppercase transition-all hover:bg-brand-400 hover:scale-[1.05] flex items-center gap-4 group/btn"
                >
                  START_COMMUNICATION
                  <ArrowUpRight className="w-5 h-5 group-hover/btn:translate-x-1 group-hover/btn:-translate-y-1 transition-transform" />
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default DetailedServices;
